import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Mail, MapPin, Send, CheckCircle } from 'lucide-react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const theme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate submission (replace with actual API call later)
    setTimeout(() => {
      setSubmitted(true);
      setLoading(false);
      setFormData({ name: '', email: '', company: '', message: '' });
    }, 1500);
  };

  return (
    <div className="contact-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@900&family=Montserrat:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root[data-theme="dark"] {
          --bg-primary: var(--bg-app);
          --bg-secondary: #141918;
          --bg-tertiary: #1F2624;
          --text-primary: #F4F1E8;
          --text-secondary: #C2BDB0;
          --text-muted: #8B8575;
          --accent-primary: #D97642;
          --accent-green: #5FD4A0;
          --border-primary: rgba(212, 196, 168, 0.15);
          --border-subtle: rgba(212, 196, 168, 0.1);
        }

        :root[data-theme="light"] {
          --bg-primary: #F8F6F1;
          --bg-secondary: #FFFFFF;
          --bg-tertiary: #E8E4DB;
          --text-primary: #1A1612;
          --text-secondary: #4A453C;
          --text-muted: #6B6558;
          --accent-primary: #D97642;
          --accent-green: #3BA876;
          --border-primary: rgba(26, 22, 18, 0.15);
          --border-subtle: rgba(26, 22, 18, 0.1);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .contact-page {
          min-height: 100vh;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .contact-content {
          flex: 1;
          max-width: 1200px;
          margin: 0 auto;
          padding: 4rem 1.5rem;
          width: 100%;
        }

        .contact-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .contact-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .contact-subtitle {
          font-size: 1.125rem;
          color: var(--text-secondary);
          max-width: 600px;
          margin: 0 auto;
        }

        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          margin-bottom: 3rem;
        }

        .contact-info {
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 1rem;
          padding: 2.5rem;
        }

        .info-item {
          display: flex;
          align-items: start;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .info-item:last-child {
          margin-bottom: 0;
        }

        .info-icon {
          width: 48px;
          height: 48px;
          background: var(--accent-primary);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .info-content h3 {
          font-weight: 600;
          font-size: 1.125rem;
          margin-bottom: 0.5rem;
        }

        .info-content p {
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .contact-form {
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 1rem;
          padding: 2.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 0.875rem 1rem;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-radius: 0.5rem;
          color: var(--text-primary);
          font-family: inherit;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .form-textarea {
          min-height: 150px;
          resize: vertical;
        }

        .btn-primary {
          width: 100%;
          padding: 0.875rem 1.75rem;
          background: var(--accent-primary);
          border: none;
          border-radius: 0.625rem;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-family: inherit;
          font-size: 1rem;
        }

        .btn-primary:hover {
          background: #C56838;
          transform: translateY(-1px);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .success-message {
          background: rgba(95, 212, 160, 0.15);
          border: 1px solid var(--accent-green);
          border-radius: 0.75rem;
          padding: 1.5rem;
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .success-title {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-weight: 600;
          color: var(--accent-green);
          margin-bottom: 0.5rem;
        }

        @media (max-width: 768px) {
          .contact-content {
            padding: 2rem 1.5rem;
          }

          .contact-title {
            font-size: 2rem;
          }

          .contact-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .contact-info,
          .contact-form {
            padding: 1.5rem;
          }
        }
      `}</style>

      <Navigation />

      <div className="contact-content">
        <div className="contact-header">
          <h1 className="contact-title">Get in Touch</h1>
          <p className="contact-subtitle">
            Interested in MIZAR for your business? We'd love to hear from you.
          </p>
        </div>

        <div className="contact-grid">
          <div className="contact-info">
            <div className="info-item">
              <div className="info-icon">
                <Mail size={24} color="white" />
              </div>
              <div className="info-content">
                <h3>Email</h3>
                <p>info@traveltxter.com</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <MapPin size={24} color="white" />
              </div>
              <div className="info-content">
                <h3>Location</h3>
                <p>United Kingdom</p>
              </div>
            </div>
          </div>

          <div className="contact-form">
            {submitted && (
              <div className="success-message">
                <div className="success-title">
                  <CheckCircle size={20} />
                  Message Sent
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Thanks for reaching out. We'll get back to you soon.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Company</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  className="form-textarea"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
